import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Star, ThumbsUp, CalendarCheck, XCircle, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import api from "@/lib/api";
import { valueLabel } from "@/i18n";

const StatCard = ({ icon: Icon, label, value, tone = "primary", testid }) => (
  <div className="bg-card border border-border rounded-xl p-6 hover:shadow-sm transition-shadow" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <Icon className={`w-5 h-5 ${tone === "accent" ? "text-accent" : "text-primary"}`} strokeWidth={1.5} />
    </div>
    <p className="font-heading text-3xl font-semibold mt-3">{value}</p>
  </div>
);

export default function DashboardHome() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  if (!stats) {
    return <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const chartData = stats.field_distribution.map((f) => {
    const full = f.field ? valueLabel(f.field) : t("common.unknown");
    return { name: full.split(" (")[0], full, count: f.count };
  });

  return (
    <div className="animate-fade-in">
      <p className="text-sm text-muted-foreground uppercase tracking-[0.2em] mb-1">{t("admin.dashboard.eyebrow")}</p>
      <h1 className="font-heading text-3xl font-semibold tracking-tight mb-8">{t("admin.dashboard.title")}</h1>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8">
        <StatCard icon={Users} label={t("admin.dashboard.total")} value={stats.total} testid="stat-total" />
        <StatCard icon={Star} label={t("status.shortlisted")} value={stats.shortlisted} tone="accent" testid="stat-shortlisted" />
        <StatCard icon={ThumbsUp} label={t("status.recommended")} value={stats.recommended} testid="stat-recommended" />
        <StatCard icon={CalendarCheck} label={t("admin.dashboard.interviewed")} value={stats.interviewed} tone="accent" testid="stat-interviewed" />
        <StatCard icon={XCircle} label={t("status.rejected")} value={stats.rejected} testid="stat-rejected" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
          <h3 className="font-heading text-lg font-medium mb-6">{t("admin.dashboard.by_field")}</h3>
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">{t("admin.dashboard.none")}</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "hsl(var(--muted))" }} formatter={(v, n, p) => [v, p.payload.full]} contentStyle={{ borderRadius: 8, border: "1px solid hsl(var(--border))", fontSize: 12 }} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]} isAnimationActive={false}>
                  {chartData.map((_, i) => <Cell key={i} fill="hsl(var(--primary))" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl p-6 flex flex-col">
          <h3 className="font-heading text-lg font-medium mb-4">{t("admin.dashboard.pipeline")}</h3>
          <div className="space-y-3 flex-1">
            {[
              { label: t("admin.dashboard.pipeline_new"), value: stats.new, color: "bg-muted-foreground" },
              { label: t("status.shortlisted"), value: stats.shortlisted, color: "bg-accent" },
              { label: t("status.recommended"), value: stats.recommended, color: "bg-primary" },
              { label: t("status.rejected"), value: stats.rejected, color: "bg-destructive" },
            ].map((row) => (
              <div key={row.label}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">{row.label}</span>
                  <span className="font-medium">{row.value}</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div className={`h-full ${row.color} rounded-full transition-all`} style={{ width: `${stats.total ? (row.value / stats.total) * 100 : 0}%` }} />
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => navigate("/admin/applications")} className="mt-6 text-sm text-primary font-medium hover:underline text-left" data-testid="view-all-applications">
            {t("admin.dashboard.view_all")}
          </button>
        </div>
      </div>
    </div>
  );
}
